// Shared exact contract for the active server-owned recommended Meta Form.
function stringValue(value) { return typeof value === 'string' ? value.trim() : ''; }

const RECOMMENDED_FORM_TEMPLATE_VERSION = 'recommended_meta_v3';
const RECOMMENDED_FORM_LOCALE = 'EN_US';
const RECOMMENDED_FORM_QUESTIONS = Object.freeze([
  Object.freeze({
    key: 'project_timing',
    label: 'When would you like to start your renovation project?',
    options: Object.freeze([
      Object.freeze({ key: 'as_soon_as_possible', label: 'As soon as possible' }),
      Object.freeze({ key: 'one_to_three_months', label: '1–3 months' }),
      Object.freeze({ key: 'three_to_six_months', label: '3–6 months' }),
      Object.freeze({ key: 'just_exploring_options', label: 'Just exploring options' }),
    ]),
  }),
  Object.freeze({ key: 'FULL_NAME', label: 'Full name' }),
  Object.freeze({ key: 'EMAIL', label: 'Email address' }),
  Object.freeze({ key: 'PHONE', label: 'Phone number' }),
]);
const RECOMMENDED_FORM_CONSENT_TITLE = 'Consent to be contacted';
const RECOMMENDED_FORM_CONSENT_BODY = 'By submitting, you agree to be contacted about your renovation request.';
const RECOMMENDED_FORM_CONSENT_CHECKBOX = 'I agree to be contacted about my renovation request.';
const RECOMMENDED_FORM_THANK_YOU_HEADLINE = 'Thanks — we received your renovation request.';
const RECOMMENDED_FORM_THANK_YOU_BODY = 'A renovation specialist will follow up shortly.';

export function safeHttpsExternalUrl(value) {
  try {
    const url = new URL(stringValue(value));
    const hostname = url.hostname.toLowerCase();
    const ipCandidate = hostname.startsWith('[') && hostname.endsWith(']')
      ? hostname.slice(1, -1)
      : hostname;
    const ipv4Parts = ipCandidate.split('.');
    const ipLiteral = ipCandidate.includes(':') || (
      ipv4Parts.length === 4
      && ipv4Parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255)
    );
    const privateIpv4 = /^(?:10\.|127\.|169\.254\.|192\.168\.|172\.(?:1[6-9]|2\d|3[01])\.)/.test(hostname);
    if (
      url.protocol !== 'https:'
      || url.username
      || url.password
      || url.hash
      || !hostname
      || hostname === 'localhost'
      || hostname.endsWith('.localhost')
      || ipLiteral
      || privateIpv4
    ) return '';
    return url.toString();
  } catch {
    return '';
  }
}

export function validRecommendedInstantFormPreview(preview, {
  privacyPolicyUrl,
  followUpUrl,
  allowExistingFormName = false,
} = {}) {
  if (!preview || typeof preview !== 'object' || Array.isArray(preview)) return false;
  const reviewedPrivacyUrl = safeHttpsExternalUrl(privacyPolicyUrl);
  const reviewedFollowUpUrl = safeHttpsExternalUrl(followUpUrl);
  const previewPrivacyUrl = safeHttpsExternalUrl(preview.privacyPolicy?.url);
  const previewFollowUpUrl = safeHttpsExternalUrl(preview.thankYou?.followUpUrl);
  const questions = Array.isArray(preview.questions) ? preview.questions : [];
  const exactQuestions = questions.length === RECOMMENDED_FORM_QUESTIONS.length
    && RECOMMENDED_FORM_QUESTIONS.every((expected, index) => (
      questions[index]?.key === expected.key
      && questions[index]?.label === expected.label
      && Object.keys(questions[index]).length === Object.keys(expected).length
      && (expected.options
        ? Array.isArray(questions[index].options)
          && questions[index].options.length === expected.options.length
          && expected.options.every((option, optionIndex) => (
            questions[index].options[optionIndex]?.key === option.key
            && questions[index].options[optionIndex]?.label === option.label
            && Object.keys(questions[index].options[optionIndex]).length === 2
          ))
        : !Object.hasOwn(questions[index], 'options'))
    ));
  const digest = stringValue(preview.templateDigest);
  const formName = stringValue(preview.formName);
  const developerAppName = stringValue(preview.developerAppName);
  const privacyLinkText = stringValue(preview.privacyPolicy?.linkText);
  const thankYouButtonText = stringValue(preview.thankYou?.buttonText);
  return (
    stringValue(preview.templateVersion) === RECOMMENDED_FORM_TEMPLATE_VERSION
    && /^sha256:[0-9a-f]{64}$/.test(digest)
    && developerAppName.length > 0
    && developerAppName.length <= 200
    && (allowExistingFormName
      ? formName.length > 0 && formName.length <= 120 && !/[\u0000-\u001f\u007f]/.test(formName)
      : / – Renovation Leads \((?:[0-9a-f]{12}|[0-9a-f]{16})\)$/.test(formName) && formName.length <= 100)
    && stringValue(preview.locale) === RECOMMENDED_FORM_LOCALE
    && exactQuestions
    && Boolean(reviewedPrivacyUrl)
    && previewPrivacyUrl === reviewedPrivacyUrl
    && privacyLinkText.length > 0
    && privacyLinkText.length <= 70
    && stringValue(preview.consent?.title) === RECOMMENDED_FORM_CONSENT_TITLE
    && stringValue(preview.consent?.body) === RECOMMENDED_FORM_CONSENT_BODY
    && stringValue(preview.consent?.checkbox?.text) === RECOMMENDED_FORM_CONSENT_CHECKBOX
    && preview.consent?.checkbox?.required === true
    && preview.consent?.checkbox?.checkedByDefault === false
    && stringValue(preview.thankYou?.headline) === RECOMMENDED_FORM_THANK_YOU_HEADLINE
    && stringValue(preview.thankYou?.body) === RECOMMENDED_FORM_THANK_YOU_BODY
    && thankYouButtonText.length > 0
    && thankYouButtonText.length <= 100
    && Boolean(reviewedFollowUpUrl)
    && previewFollowUpUrl === reviewedFollowUpUrl
  );
}
